import { Stack, Typography } from '@mui/material';
import { PropsWithChildren, ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';

import { COLORS } from '@/styles/tokens/colors';
import { WithClasses } from '@/styles/types';

import { Button } from '../form/baseInputs/Button';
import { CloseButton } from '../form/baseInputs/Button/CloseButton';
import { LinkButton } from '../form/baseInputs/Link/LinkButton';
import { ChevronLeftIcon } from '../icons/ChevronLeftIcon';
import { Badge, BadgeVariants } from '../notifications/Badge/Badge';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '16px',
    gap: '16px',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${COLORS.GRAY[200]}`,
  },
  contentWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    justifyContent: 'space-between',
  },
  title: {},
  subtitle: {},
  backButton: {
    padding: 0,
    margin: 0,
  },
  backIcon: {
    color: COLORS.TEXT.SECONDARY,
  },
}));

export interface HeaderProps {
  title?: string;
  subtitle?: string;
  /** Optional label for a back action rendered above the title. */
  backLabel?: string;
  count?: number;
  totalCount?: number;
  /** Optional node rendered inline immediately after the title (e.g. an icon button). */
  titleAdornment?: ReactNode;
  rightHeaderContent?: ReactNode;
  leftHeaderContent?: ReactNode;
  onClose?: () => void;
  classes?: WithClasses<typeof useStyles>;
  onBack?: () => void;
}

export const Header = ({
  title,
  subtitle,
  backLabel,
  count,
  totalCount,
  onClose,
  titleAdornment,
  rightHeaderContent,
  leftHeaderContent,
  children,
  onBack,
  ...props
}: PropsWithChildren<HeaderProps>) => {
  const { classes } = useStyles(undefined, { props });

  const titleContent = title && (
    <Stack direction="row" gap={1} alignItems="center">
      <Typography variant="title-l" className={classes.title}>
        {title}
      </Typography>
      {titleAdornment}
    </Stack>
  );
  const defaultLeftContent = (
    <Stack direction="row" gap={1} alignItems="center">
      {onBack && (
        <Button
          square
          size="sm"
          variant="transparent"
          onClick={() => onBack()}
          className={classes.backButton}
          data-testid="HeaderBackButton"
        >
          <ChevronLeftIcon className={classes.backIcon} />
        </Button>
      )}
      <div>
        {subtitle && (
          <Typography variant="body-xs" className={classes.subtitle}>
            {subtitle}
          </Typography>
        )}
        {titleContent}
      </div>
    </Stack>
  );
  const labeledBackLeftContent = onBack && backLabel && (
    <Stack gap={1} alignItems="flex-start">
      <LinkButton
        display={backLabel}
        underline={false}
        startIcon={<ChevronLeftIcon size={16} />}
        onClick={onBack}
        className={classes.backButton}
        data-testid="HeaderBackButton"
      />
      {titleContent}
    </Stack>
  );
  let leftContent = leftHeaderContent ?? defaultLeftContent;
  if (!leftHeaderContent && labeledBackLeftContent) {
    leftContent = labeledBackLeftContent;
  }

  const rightContent = rightHeaderContent ?? (
    <>
      {count && totalCount && (
        <Badge
          variant={BadgeVariants.Primary}
          display={`${count} of ${totalCount}`}
        />
      )}
      {onClose && (
        <CloseButton
          size="sm"
          variant="transparent"
          onClick={onClose}
          data-testid="HeaderCloseButton"
        />
      )}
    </>
  );

  return (
    <header className={classes.root}>
      <div className={classes.contentWrapper}>
        {leftContent}
        {rightContent}
      </div>
      {children}
    </header>
  );
};
